import { useEffect, useRef } from 'react'
import type { FormatFamily } from './formats'
import { computeRoi } from './roi'

interface Props {
  videoRef: React.RefObject<HTMLVideoElement | null>
  family: FormatFamily
  zoom: number
  corners: { x: number; y: number }[] | null
}

/**
 * Draws the aiming guide and highlights a detected code.
 *
 * On a canvas rather than SVG/DOM: this repaints on every detection, and
 * mutating DOM nodes at that rate competes with the decode loop for main-thread
 * time on exactly the frames where responsiveness matters most.
 *
 * The guide is drawn from the *same* `computeRoi` the decoder uses, so what is
 * outlined on screen is genuinely what gets decoded. Drawing a decorative box
 * that doesn't match the real search region is actively misleading — the user
 * lines the code up inside it and it still doesn't scan.
 */
export function Overlay({ videoRef, family, zoom, corners }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const frameRef = useRef<number | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const video = videoRef.current
    if (canvas === null || video === null) return

    const ctx = canvas.getContext('2d')
    if (ctx === null) return

    const draw = (): void => {
      const vw = video.videoWidth
      const vh = video.videoHeight
      const rect = video.getBoundingClientRect()

      if (vw === 0 || vh === 0 || rect.width === 0) {
        frameRef.current = requestAnimationFrame(draw)
        return
      }

      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const cw = Math.round(rect.width * dpr)
      const ch = Math.round(rect.height * dpr)
      if (canvas.width !== cw || canvas.height !== ch) {
        canvas.width = cw
        canvas.height = ch
        canvas.style.width = `${rect.width}px`
        canvas.style.height = `${rect.height}px`
      }

      // The preview uses object-fit: cover, so the video is scaled to fill and
      // cropped. Reproduce that transform exactly, or the highlight sits offset
      // from the code by however much is being cropped.
      const scale = Math.max(rect.width / vw, rect.height / vh) * dpr
      const offsetX = (cw - vw * scale) / 2
      const offsetY = (ch - vh * scale) / 2
      const toCanvas = (x: number, y: number) => ({
        x: offsetX + x * scale,
        y: offsetY + y * scale,
      })

      ctx.clearRect(0, 0, cw, ch)

      const roi = computeRoi(family, vw, vh, zoom)
      const tl = toCanvas(roi.sx, roi.sy)
      const br = toCanvas(roi.sx + roi.sw, roi.sy + roi.sh)
      const boxW = br.x - tl.x
      const boxH = br.y - tl.y

      // Dim outside the search region so it reads as "point this bit at it".
      ctx.fillStyle = 'rgba(2, 6, 23, 0.45)'
      ctx.beginPath()
      ctx.rect(0, 0, cw, ch)
      ctx.rect(tl.x, tl.y, boxW, boxH)
      ctx.fill('evenodd')

      if (corners !== null && corners.length >= 4) {
        // Detected: fill and outline the actual code quadrilateral.
        ctx.beginPath()
        const first = toCanvas(corners[0]!.x, corners[0]!.y)
        ctx.moveTo(first.x, first.y)
        for (const point of corners.slice(1)) {
          const p = toCanvas(point.x, point.y)
          ctx.lineTo(p.x, p.y)
        }
        ctx.closePath()
        ctx.fillStyle = 'rgba(16, 185, 129, 0.25)'
        ctx.fill()
        ctx.strokeStyle = '#10b981'
        ctx.lineWidth = 3 * dpr
        ctx.lineJoin = 'round'
        ctx.stroke()
      } else {
        // Idle: corner brackets only. A full rectangle invites people to line
        // the code up precisely, which is the behaviour we are trying to remove.
        const len = Math.min(boxW, boxH) * 0.16
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)'
        ctx.lineWidth = 3 * dpr
        ctx.lineCap = 'round'
        const corner = (x: number, y: number, dx: number, dy: number): void => {
          ctx.beginPath()
          ctx.moveTo(x + dx * len, y)
          ctx.lineTo(x, y)
          ctx.lineTo(x, y + dy * len)
          ctx.stroke()
        }
        corner(tl.x, tl.y, 1, 1)
        corner(br.x, tl.y, -1, 1)
        corner(tl.x, br.y, 1, -1)
        corner(br.x, br.y, -1, -1)
      }

      frameRef.current = requestAnimationFrame(draw)
    }

    frameRef.current = requestAnimationFrame(draw)
    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }
  }, [videoRef, family, zoom, corners])

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute inset-0 h-full w-full"
      aria-hidden="true"
    />
  )
}
